import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, Pill } from "@/components/PageShellParts";
import PilotInquiryForm from "@/components/PilotInquiryForm";
import { Beaker, Briefcase, Building2, CalendarCheck, ClipboardCheck, GraduationCap, Handshake, Landmark, LineChart, MessageSquare, Search, Sparkles, UserCheck, Workflow } from "lucide-react";

const Icons = { Beaker, Briefcase, Building2, CalendarCheck, ClipboardCheck, GraduationCap, Handshake, Landmark, LineChart, MessageSquare, Search, Sparkles, UserCheck, Workflow };

const AUDIENCES = [
  { icon: Icons.Building2, title: "Mental health clinics", desc: "Structured intake and assessment workflows for clinical teams." },
  { icon: Icons.UserCheck, title: "Psychologists and assessment professionals", desc: "AI-assisted support for structured evaluation and documentation." },
  { icon: Icons.GraduationCap, title: "Schools and universities", desc: "Privacy-aware student wellbeing and support workflows." },
  { icon: Icons.Briefcase, title: "Companies and wellbeing teams", desc: "Employee wellbeing check-ins with clear ethical boundaries." },
  { icon: Icons.Landmark, title: "Municipalities and public innovation", desc: "Pilot structured support pathways in public services." },
  { icon: Icons.Beaker, title: "Research and innovation partners", desc: "Collaborate on responsible-AI research in sensitive domains." },
];

const EXPLORES = [
  "Digital intake or pre-assessment workflows",
  "Structured assessment summaries",
  "AI-assisted insight generation with human review",
  "Professional review and sign-off workflows",
  "User experience and engagement patterns",
  "Privacy-aware analytics",
  "Reporting formats for internal stakeholders",
];

const WE_PROVIDE = [
  "A guided demo environment",
  "Structured onboarding support",
  "The core assessment flow",
  "The AI assistant explanation layer",
  "Admin tools and contact support",
  "Feedback collection mechanisms",
  "A pilot evaluation summary at the end",
];

const PARTNERS_PROVIDE = [
  "Use-case context and goals",
  "Feedback from professionals and users",
  "Ethical and operational requirements",
  "Evaluation criteria and success metrics",
  "Appropriate consent processes for participants",
];

const STEPS = [
  { icon: Icons.MessageSquare, title: "Discovery call", desc: "Align on context, goals, stakeholders, and constraints." },
  { icon: Icons.Search, title: "Use-case definition", desc: "Decide scope, workflow boundaries, and safety guardrails together." },
  { icon: Icons.Workflow, title: "Pilot setup", desc: "Configure the demo environment and onboarding materials." },
  { icon: Icons.LineChart, title: "Feedback and evaluation", desc: "Collect structured feedback from professionals and users." },
  { icon: Icons.Handshake, title: "Next-step proposal", desc: "Review findings and agree on whether/how to continue." },
];

export default function PilotProgramPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="Pilot Program" description="Explore PsyTech pilot opportunities for clinics, education, companies, municipalities, and innovation partners." />
      <div className="pt-20">
      <PageHero
        eyebrow="Pilot Program"
        title="PsyTech Pilot Program"
        subtitle="Collaborate with PsyTech to explore AI-assisted mental health assessment workflows in a structured, responsible, and privacy-aware way."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Structured evaluation</Pill>
          <Pill tone="cyan">Professional oversight</Pill>
          <Pill tone="cyan">Privacy-aware</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading
          eyebrow="Who the pilot is for"
          title="Organizations exploring responsible AI in mental health workflows"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AUDIENCES.map((a) => (
            <FeatureCard key={a.title} icon={a.icon} title={a.title}>{a.desc}</FeatureCard>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <SectionHeading eyebrow="What a pilot can explore" title="Areas we can test together" />
            <ul className="space-y-2">
              {EXPLORES.map((x) => (
                <li key={x} className="flex items-start gap-3 text-sm text-slate-700">
                  <Sparkles className="w-4 h-4 text-cyan-700 mt-0.5 flex-shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="Pilot boundaries" title="What pilots are not" />
            <ul className="space-y-2 text-sm text-slate-700">
              {[
                "Not for emergency use",
                "Not for clinical diagnosis",
                "Not for replacing professionals",
                "Clinical decisions remain with qualified professionals",
                "Pilots must follow appropriate consent, privacy, and professional oversight",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <ClipboardCheck className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <SectionHeading eyebrow="What PsyTech provides" title="Our contribution" />
            <ul className="space-y-2 text-sm text-slate-700">
              {WE_PROVIDE.map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <CalendarCheck className="w-4 h-4 text-cyan-700 mt-0.5 flex-shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="What partners provide" title="Your contribution" />
            <ul className="space-y-2 text-sm text-slate-700">
              {PARTNERS_PROVIDE.map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <Handshake className="w-4 h-4 text-cyan-700 mt-0.5 flex-shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="Pilot process"
          title="A structured 5-step path"
          center
        />
        <ol className="grid md:grid-cols-5 gap-4">
          {STEPS.map((s, idx) => (
            <li
              key={s.title}
              className="relative bg-white border border-slate-200 rounded-2xl p-5"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/15 to-cyan-600/10 ring-1 ring-cyan-200 flex items-center justify-center mb-3">
                <s.icon className="w-4 h-4 text-cyan-700" />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold mb-1">
                Step {idx + 1}
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1 font-['Plus_Jakarta_Sans']">
                {s.title}
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="pilot-inquiry">
        <SectionHeading
          eyebrow="Pilot inquiry"
          title="Start the conversation"
          subtitle="Tell us about your organization and interest. We review every inquiry personally and get back to you within a few business days."
          center
        />
        <PilotInquiryForm />
      </Section>
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
